import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Sparkle, FileArrowDown, CircleNotch, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function PaperDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/papers/${id}`).then((r) => setP(r.data)); }, [id]);

  async function summarize() {
    setBusy(true);
    try {
      const { data } = await api.post(`/papers/${id}/summarize`);
      setP({ ...p, ai_summary: data.ai_summary });
      toast.success(data.cached ? "Summary loaded" : "Summary generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Summarization failed");
    } finally {
      setBusy(false);
    }
  }

  if (!p) return <div className="px-6 md:px-10 lg:px-16 py-16">Loading…</div>;

  return (
    <div data-testid="paper-detail" className="px-6 md:px-10 lg:px-16 py-10 md:py-12">
      <Link to="/papers" data-testid="back-to-papers" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 mb-10">
        <ArrowLeft size={14} /> All papers
      </Link>

      <div className="grid md:grid-cols-12 gap-10 md:gap-14">
        <div className="md:col-span-8">
          <div className="flex items-center gap-2 mb-4">
            {p.track && <span className="tag-brand">{p.track}</span>}
            {p.conference && <span className="tag">{p.conference.title}</span>}
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-[-0.04em] leading-[.95]" data-testid="paper-title">{p.title}</h1>
          <div className="mt-5 text-base text-zinc-700 font-mono">{(p.authors || []).join(" · ")}</div>
          {p.conference && (
            <div className="mt-2 text-sm text-zinc-500">
              Presented at <Link to={`/conferences/${p.conference.id}`} className="underline underline-offset-2 hover:text-blue-700">{p.conference.title}</Link>
            </div>
          )}

          {p.ai_summary && (
            <div className="ai-block mt-10" data-testid="paper-ai-summary">
              <div className="flex items-center justify-between mb-4">
                <span className="ai-block-label">
                  <Sparkle size={12} weight="fill" /> AI Generated Summary
                </span>
                <button onClick={summarize} className="text-[10px] font-mono uppercase tracking-wider text-blue-700 hover:text-blue-900" data-testid="ai-regenerate-btn">Regenerate ↻</button>
              </div>
              <p className="text-base leading-relaxed text-zinc-800 whitespace-pre-line">{p.ai_summary}</p>
            </div>
          )}

          <div className="mt-12 border-t border-zinc-200 pt-8">
            <div className="overline mb-4">Abstract</div>
            <p className="text-lg leading-relaxed text-zinc-800 whitespace-pre-line max-w-prose">{p.abstract || "No abstract available."}</p>
          </div>
        </div>

        <aside className="md:col-span-4">
          <div className="md:sticky md:top-24 space-y-4">
            <button onClick={summarize} disabled={busy || !p.abstract} data-testid="paper-summarize-btn" className="btn-accent w-full justify-center">
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="duotone" />}
              {p.ai_summary ? "Regenerate AI summary" : "Generate AI summary"}
            </button>
            {p.pdf_url && (
              <a href={p.pdf_url} target="_blank" rel="noreferrer" data-testid="paper-pdf-link" className="btn-secondary w-full justify-center">
                <FileArrowDown size={16} /> Read full paper <ArrowUpRight size={12} />
              </a>
            )}

            <div className="border border-zinc-200 p-5 bg-[#FAFAFA] mt-6">
              <div className="overline mb-2">Citation</div>
              <div className="font-mono text-xs text-zinc-600 leading-relaxed">
                Paper № {p.id.slice(0, 6)}<br />
                Symposia Index · {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
